import React, { useState, useEffect } from 'react';
import { ServiceHealthChecker } from '../services';
import { API_BASE_URL } from '../services/index';
import PermissionService from '../services/permissionService';
import RecipesService from '../services/recipesService';
import RecipeBookApi from '../services/recipeBookApi';
import RecipePdfViewer from './RecipeBook/RecipePdfViewer';
import { scrollToTop } from '../utils/scroll';
import SubscriptionWindowModal from './SubscriptionWindowModal';
import MySubscriptionModal from './MySubscriptionModal';
import EmailAlertsMenuRow from './EmailAlertsMenuRow';
import EmailLogModal from './EmailLogModal';
import { exportAllRecipesPdf, exportAllRecipesXlsx, exportIngredientsTemplateXlsx, exportMarkedReviewPdf, fetchSavedPreparationsForExport } from '../utils/reviewPdfExport';
import useBanquetFlag from '../hooks/useBanquetFlag';

const IS_PRODUCTION_BUILD = process.env.NODE_ENV === 'production';
const LOCAL_ONLY_REPORT_NAV_ITEMS = new Set([]);

const Navigation = ({ currentScreen, goTo, tsrLockActive = false, allowedScreens = [], subscriptionLocked = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  // Structured Recipe Book availability. Defaults to OFF and stays off unless the
  // server says otherwise, so with the feature flag down this component behaves
  // exactly as it did before the feature existed (spec RULE 7).
  const [recipeBookAccess, setRecipeBookAccess] = useState({ enabled: false, isOwner: false });
  const [isExportingReviewPdf, setIsExportingReviewPdf] = useState(false);
  const [isExportingAllRecipesPdf, setIsExportingAllRecipesPdf] = useState(false);
  const [isExportingAllRecipesXlsx, setIsExportingAllRecipesXlsx] = useState(false);
  const [isExportingIngredientsXlsx, setIsExportingIngredientsXlsx] = useState(false);
  const [isExportingJson, setIsExportingJson] = useState(false);
  
  const [showDateModal, setShowDateModal] = useState(false);
  const [exportTarget, setExportTarget] = useState(null); // 'pdf' | 'excel' | 'json'
  const [selectedPeriod, setSelectedPeriod] = useState('all'); // 'all' | 'yesterday' | 'month' | 'prev-month' | 'custom'
  const [categoryGrouping, setCategoryGrouping] = useState('sub'); // 'sub' | 'master' — how category-based exports are grouped
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // INVEXIS-admin-only: calendar to move a customer's 1-month subscription window.
  // Visibility is decided by the backend (INVEXIS_ADMIN_EMAILS), never by localStorage.
  const [isInvexisAdmin, setIsInvexisAdmin] = useState(false);
  const [showSubscriptionWindow, setShowSubscriptionWindow] = useState(false);
  // Self-service for every team member of a business (server-decided; '*' = all).
  const [canManageOwnSubscription, setCanManageOwnSubscription] = useState(true);
  const [showMySubscription, setShowMySubscription] = useState(false);
  // INVEXIS-admin-only: a business's last 100 emails and their results, plus previews.
  const [showEmailLog, setShowEmailLog] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    fetch(`${API_BASE_URL}/subscription-window/access`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { isAdmin: false, selfService: true }))
      .then((d) => {
        if (cancelled) return;
        setIsInvexisAdmin(d?.isAdmin === true);
        setCanManageOwnSubscription(d?.selfService !== false);
      })
      .catch(() => { if (!cancelled) { setIsInvexisAdmin(false); setCanManageOwnSubscription(true); } });
    return () => { cancelled = true; };
  // Re-checked each time the menu opens, so a login switch or token refresh
  // is picked up without a full page reload.
  }, [isOpen]);
  // The generated Recipe Book, held for preview. Non-null means the viewer is open.
  // Nothing reaches the user's disk while this is set — only the viewer's Download
  // button writes a file.
  const [pdfPreview, setPdfPreview] = useState(null);

  // Fetch user permissions from Redis on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const permissions = await PermissionService.getUserPermissions();
        setUserPermissions(permissions);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, []);

  // One probe, on mount. RecipeBookApi.getConfig never throws — a failure answers
  // "off", which keeps the old behaviour rather than breaking the menu.
  useEffect(() => {
    let cancelled = false;
    RecipeBookApi.getConfig().then((access) => {
      if (!cancelled) setRecipeBookAccess(access);
    });
    return () => { cancelled = true; };
  }, []);

  /**
   * Is the editable Structured Recipe Book available to this user?
   *
   * Only when the feature flag is on AND the server confirms they are the owner
   * (spec §7.1). False is the safe default: the nav entry is simply not rendered, and
   * every other Recipe menu item behaves exactly as it always has.
   */
  const structuredBookAvailable = recipeBookAccess.enabled && recipeBookAccess.isOwner;

  // Restaurant + Banquet businesses only. False until the server says otherwise.
  const banquetEnabled = useBanquetFlag();

  // Helper function to check if user has permission
  const hasPermission = (permissionNames) => {
    if (!userPermissions || userPermissions.length === 0) return false;
    return permissionNames.some(name => 
      userPermissions.some(p => p.permission_name === name)
    );
  };

  const allNavigationItems = [
    { id: 'overview', label: '📊 Overview', category: 'Main', permissions: [] },
    // The cycle-wise, single-ingredient audit. Still the right tool for
    // investigating one ingredient in detail — it is now reached FROM the
    // monthly report rather than being the way in (spec §0.3, §9.1).
    { id: 'leakage', label: 'Ingredient audit', category: 'Main', permissions: ['view_reports'] },
    // The monthly leakage tracker is its OWN product, not an EOM report: it is
    // calculated once by a button and locked, where the EOM reports below are
    // live reads. Keeping it in the EOM Reports group made the two look like one
    // screen and cost the Leakage EOM Report its place in the list.
    { id: 'leakage-monthly', label: 'Kitchen Leakage', category: 'Main', permissions: ['view_reports'] },
    { id: 'sales-eom', label: 'Sales EOM Report', category: 'EOM Reports', permissions: ['view_reports'] },
    { id: 'wastage-eom', label: 'Wastage EOM Report', category: 'EOM Reports', permissions: ['view_reports'] },
    { id: 'expense-eom', label: 'Expense EOM Report', category: 'EOM Reports', permissions: ['view_reports'] },
    { id: 'leakage-eom', label: 'Leakage EOM Report', category: 'EOM Reports', permissions: ['view_reports'] },
    // Its own group, not EOM Reports: the banquet month is built from banquet
    // records only and shares nothing with the four reports above.
    ...(banquetEnabled ? [{ id: 'banquet-eom', label: 'Banquet EOM Report', category: 'Banquet Reports', permissions: ['view_reports'] }] : []),
    { id: 'xyz', label: '✨ XYZ Analysis', category: 'Main', permissions: ['view_reports'] },
    { id: 'recipe-library', label: '📖 Recipe Library', category: 'Main', permissions: [] },
    { id: 'inventory-calculator', label: '🧮 Inventory Calculator', category: 'Tools', permissions: ['view_inventory'] },
    { id: 'ocr', label: '📄 OCR Scanner', category: 'Tools', permissions: ['stock_in'] },
    
    // QR Billing Section
    { id: 'qr-billing-onboarding', label: ' QR Onboarding', category: 'QR Billing', permissions: [] },
    { id: 'qr-owner-dashboard', label: '👨‍💼 Owner Dashboard', category: 'QR Billing', permissions: ['view_orders', 'view_sales'] },
    { id: 'waiter-dashboard', label: '🧑‍🍳 Waiter Dashboard', category: 'QR Billing', permissions: ['view_orders'] },
    { id: 'chef-dashboard', label: '👨‍🍳 Chef Dashboard', category: 'QR Billing', permissions: ['view_orders', 'manage_orders'] },
    { id: 'qr-menu-management', label: '🍽️ Menu Management', category: 'QR Billing', permissions: ['manage_menu'] },
    { id: 'qr-itemmap', label: '🍴 QR Item Mapping', category: 'QR Billing', permissions: ['manage_recipes'] },
    
    // Inventory Section
    { id: 'stock-in', label: ' Stock In', category: 'Inventory', permissions: ['stock_in'] },
    { id: 'stock-out', label: '📤 Stock Out', category: 'Inventory', permissions: ['stock_out'] },
    { id: 'minimal-stock', label: '🛡️ Minimal Stock', category: 'Inventory', permissions: ['view_inventory'] },
    { id: 'create-reorder', label: ' Create Reorder', category: 'Inventory', permissions: ['create_po'] },
    
    // Operations Section
    { id: 'usage', label: '🍽️ Usage', category: 'Operations', permissions: ['view_inventory'] },
    { id: 'todays-sales-report', label: '📈 Today\'s Sales', category: 'Operations', permissions: ['view_sales', 'manage_sales'] },
    
    // Management Section
    { id: 'wastage-thresholds', label: '⚠️ Wastage Thresholds', category: 'Management', permissions: ['manage_settings'] },
    { id: 'wastage-threshold-approvals', label: '✅ Wastage Approvals', category: 'Management', permissions: ['manage_settings'] },
    { id: 'vendors', label: '🏪 Vendors', category: 'Management', permissions: ['view_vendors'] },
    
    // Setup Section
    { id: 'itemmap', label: '🍴 Item Mapping', category: 'Setup', permissions: ['manage_recipes'] },
    { id: 'customization-mapping', label: '🎛️ Customization Mapping', category: 'Setup', permissions: ['manage_menu'] },
    { id: 'mark-as-review-export', label: '📝 Mark as Review', category: 'Setup', permissions: ['manage_recipes'] },
    { id: 'recipe-download-export', label: '📥 Recipe Download', category: 'Setup', permissions: ['manage_recipes'] },
    // Spec §12.9 A. Its own entry rather than a mode hidden inside Recipe Download:
    // that entry views the PRINTED book, this one opens the EDITABLE one, and a single
    // menu item that did either depending on who clicked it was the confusing option.
    // ownerOnly + featureFlag are filtered below; with the flag off it is not rendered.
    { id: 'structured-recipe-book', label: '📖 Structured Recipe Book', category: 'Setup', permissions: ['manage_recipes'], ownerOnly: true, featureFlag: 'STRUCTURED_RECIPE_BOOK_ENABLED' },
    { id: 'recipe-excel-export', label: '📊 Excel Recipe', category: 'Setup', permissions: ['manage_recipes'] },
    { id: 'recipe-json-download', label: '💾 Backup Recipes (JSON)', category: 'Setup', permissions: ['manage_recipes'] },
    { id: 'ingredients-download-export', label: '📄 Ingredients', category: 'Setup', permissions: ['manage_recipes', 'manage_inventory'] },
    // FEATURE DISABLED: VID Manager - To re-enable, uncomment below
    // { id: 'vid-manager', label: ' V.I.D. Manager', category: 'Setup', permissions: ['manage_inventory'] },
    { id: 'settings', label: '⚙️ Settings', category: 'Management', permissions: ['view_settings'] },
    // Subscription Section - available for all Business IDs
    { id: 'my-subscription', label: '🗓️ My Subscription Period', category: 'Subscription', permissions: [] },
    // Shown only when the backend confirms the logged-in email is an INVEXIS admin.
    ...(isInvexisAdmin ? [
      { id: 'subscription-window', label: '🗓️ Subscription Window', category: 'INVEXIS Admin', permissions: [], adminOnly: true },
      { id: 'email-log', label: '📧 Email Log', category: 'INVEXIS Admin', permissions: [], adminOnly: true }
    ] : [])
  ];

  // Filter navigation items based on user permissions
  const navigationItems = loadingPermissions 
    ? [] 
    : allNavigationItems.filter(item => {
        if (IS_PRODUCTION_BUILD && LOCAL_ONLY_REPORT_NAV_ITEMS.has(item.id)) {
          return false;
        }

        // Owner-gated items. The smallest possible filter alongside the existing
        // permission one (spec §12.9 A says not to refactor the filter): an item that
        // asks for ownerOnly is hidden unless the server said this user is an owner
        // AND its feature flag is on. Items without the field are unaffected.
        if (item.ownerOnly && !structuredBookAvailable) return false;

        // Always show items with no permission requirements
        if (!item.permissions || item.permissions.length === 0) return true;

        // Check if user has at least one of the required permissions
        return hasPermission(item.permissions);
      });

  const categories = {
    'Main': '🏠',
    'Inventory': '',
    'Operations': '⚙️',
    'QR Billing': '🧾',
    'Banquet Reports': '🍽️',
    'Tools': '🔧',
    'Management': '👥',
    'Setup': '⚙️',
    'Features': '',
    'Debug': '🐛',
    'INVEXIS Admin': '🛠️',
    'Subscription': '💳'
  };

  const checkHealth = async () => {
    setIsCheckingHealth(true);
    try {
      const results = await ServiceHealthChecker.checkAllServices();
      setHealthStatus(results);
    } catch (error) {
      console.error('Health check failed:', error);
    } finally {
      setIsCheckingHealth(false);
    }
  };

  const groupedItems = navigationItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  const handleReviewPdfExport = async () => {
    if (isExportingReviewPdf) return;

    setIsExportingReviewPdf(true);

    try {
      const businessId = typeof window !== 'undefined'
        ? window.localStorage.getItem('businessId')
        : null;

      await exportMarkedReviewPdf({ businessId });
    } catch (error) {
      console.error('Failed to export Mark as Review PDF:', error);
      alert(error?.message || 'Failed to export Mark as Review PDF. Please try again.');
    } finally {
      setIsExportingReviewPdf(false);
    }
  };

  const getExportDates = () => {
    if (selectedPeriod === 'all') {
      return { startDate: null, endDate: null };
    }
    
    const today = new Date();
    
    if (selectedPeriod === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const dateStr = yesterday.toISOString().slice(0, 10);
      return { startDate: `${dateStr} 00:00:00`, endDate: `${dateStr} 23:59:59` };
    }
    
    if (selectedPeriod === 'month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { 
        startDate: `${firstDay.toISOString().slice(0, 10)} 00:00:00`, 
        endDate: `${lastDay.toISOString().slice(0, 10)} 23:59:59` 
      };
    }
    
    if (selectedPeriod === 'prev-month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
      return { 
        startDate: `${firstDay.toISOString().slice(0, 10)} 00:00:00`, 
        endDate: `${lastDay.toISOString().slice(0, 10)} 23:59:59` 
      };
    }
    
    if (selectedPeriod === 'custom') {
      if (!customStart || !customEnd) {
        alert('Please select both start and end dates.');
        return null;
      }
      return { startDate: `${customStart} 00:00:00`, endDate: `${customEnd} 23:59:59` };
    }
    
    return { startDate: null, endDate: null };
  };

  const handleConfirmExport = async () => {
    // PDF & Excel always export everything — only the grouping is selectable.
    // The period picker applies to the JSON backup alone.
    if (exportTarget === 'pdf') {
      setShowDateModal(false);
      await handleAllRecipesPdfExport(null, null, categoryGrouping);
      return;
    }

    if (exportTarget === 'excel') {
      setShowDateModal(false);
      await handleAllRecipesExcelExport(null, null, categoryGrouping);
      return;
    }

    const dates = getExportDates();
    if (!dates && selectedPeriod === 'custom') return; // validation failed

    const { startDate, endDate } = dates || {};

    setShowDateModal(false);

    if (exportTarget === 'json') {
      await handleRecipeJsonExport(startDate, endDate);
    }
  };

  /**
   * Recipe Download → build the book, then SHOW it.
   *
   * The generator used to end in `pdf.save()`, so choosing "Recipe Download" put a file
   * in the Downloads folder whether or not the user wanted one — there was no way to
   * simply look at the book. Asking for `output: 'blob'` returns the document instead;
   * the viewer renders it and owns the Download button.
   *
   * The generation itself is completely unchanged, so the file the viewer saves is the
   * same file this used to download.
   */
  const handleAllRecipesPdfExport = async (startDate = null, endDate = null, groupMode = 'sub') => {
    if (isExportingAllRecipesPdf) return;

    setIsExportingAllRecipesPdf(true);

    try {
      const businessId = typeof window !== 'undefined'
        ? window.localStorage.getItem('businessId')
        : null;

      const result = await exportAllRecipesPdf({ businessId, startDate, endDate, groupMode, output: 'blob' });

      if (result && result.blob) {
        setPdfPreview({
          blob: result.blob,
          fileName: result.fileName,
          pageCount: result.pageCount,
          subtitle: groupMode === 'master' ? 'Grouped by master category' : 'Grouped by subcategory',
        });
      } else {
        // The generator answered without a document. Say so rather than opening an
        // empty viewer the user has to work out the meaning of.
        alert('The recipe book could not be prepared. Please try again.');
      }
    } catch (error) {
      console.error('Failed to prepare Recipe Download PDF:', error);
      alert(error?.message || 'Failed to prepare the recipe book. Please try again.');
    } finally {
      setIsExportingAllRecipesPdf(false);
    }
  };

  const handleAllRecipesExcelExport = async (startDate = null, endDate = null, groupMode = 'sub') => {
    if (isExportingAllRecipesXlsx) return;

    setIsExportingAllRecipesXlsx(true);

    try {
      const businessId = typeof window !== 'undefined'
        ? window.localStorage.getItem('businessId')
        : null;

      await exportAllRecipesXlsx({ businessId, startDate, endDate, groupMode });
    } catch (error) {
      console.error('Failed to export Excel Recipe:', error);
      alert(error?.message || 'Failed to export Excel Recipe. Please try again.');
    } finally {
      setIsExportingAllRecipesXlsx(false);
    }
  };

  const handleRecipeJsonExport = async (startDate = null, endDate = null) => {
    if (isExportingJson) return;

    setIsExportingJson(true);

    try {
      const businessId = typeof window !== 'undefined'
        ? window.localStorage.getItem('businessId')
        : null;
      if (!businessId) {
        throw new Error('Business ID not found. Please login again.');
      }

      const [exportData, savedPreparations] = await Promise.all([
        RecipesService.getExportData(businessId, startDate, endDate),
        fetchSavedPreparationsForExport(businessId)
      ]);

      // Map to seedable structure
      const seedableData = exportData.map(recipe => ({
        name: recipe.name || '',
        displayName: recipe.display_name || recipe.name || '',
        sourceLanguage: recipe.source_language || 'en',
        price: Number(recipe.price || 0),
        servings: Number(recipe.servings || 1),
        portionMultiplier: Number(recipe.portion_multiplier || 1.0),
        instructions: recipe.instructions || 'Instructions',
        prep_time_minutes: Number(recipe.prep_time_minutes || 0),
        cook_time_minutes: Number(recipe.cook_time_minutes || 0),
        category: recipe.category || '',
        image_url: recipe.image_url || null,
        description: recipe.description || '',
        is_active: recipe.is_active !== undefined ? recipe.is_active : true,
        complimentary_items: Array.isArray(recipe.complimentary_items) ? recipe.complimentary_items : [],
        add_ons: Array.isArray(recipe.add_ons) ? recipe.add_ons : [],
        ingredients: (recipe.ingredients || []).map(ing => ({
          name: ing.ingredient_name || '',
          quantity: Number(ing.quantity || 0),
          unit: ing.unit || 'g',
          notes: ing.notes || ''
        }))
      }));

      // Backup format: recipes plus EVERY saved preparation template in the DB
      // (full batch quantities), matching the PDF/Excel saved-prep sections.
      const backupData = {
        recipes: seedableData,
        savedPreparations: (savedPreparations || []).map((prep) => ({
          name: prep.preparationName,
          batchQuantity: prep.totalQuantity,
          batchUnit: prep.totalUnit,
          ingredients: prep.ingredients
        }))
      };

      const businessDisplayName = exportData[0]?.business_name || `Business_${businessId}`;
      const fileTimestamp = new Date().toISOString().slice(0, 10);
      const filename = `${businessDisplayName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_recipes_${fileTimestamp}.json`;

      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Recipe JSON:', error);
      alert(error?.message || 'Failed to export Recipe JSON. Please try again.');
    } finally {
      setIsExportingJson(false);
    }
  };

  const handleIngredientsTemplateExport = async () => {
    if (isExportingIngredientsXlsx) return;

    setIsExportingIngredientsXlsx(true);

    try {
      const businessId = typeof window !== 'undefined'
        ? window.localStorage.getItem('businessId')
        : null;

      await exportIngredientsTemplateXlsx({ businessId });
    } catch (error) {
      console.error('Failed to export Ingredients template:', error);
      alert(error?.message || 'Failed to export Ingredients template. Please try again.');
    } finally {
      setIsExportingIngredientsXlsx(false);
    }
  };

  // Context for the export period popup — title/accent adapt to what's being exported.
  const EXPORT_META = {
    excel: { icon: '📊', title: 'Export Recipe Book', subtitle: 'Excel workbook · one sheet per category', accent: '#2e7d32', accentSoft: '#eaf5ec', gradient: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)' },
    pdf: { icon: '📄', title: 'Recipe Download', subtitle: 'Printable PDF recipe book', accent: '#2196f3', accentSoft: '#eaf4fe', gradient: 'linear-gradient(135deg, #1e88e5 0%, #42a5f5 100%)' },
    json: { icon: '💾', title: 'Backup Recipes', subtitle: 'JSON backup file', accent: '#475569', accentSoft: '#eef2f7', gradient: 'linear-gradient(135deg, #475569 0%, #64748b 100%)' }
  };
  const exportMeta = EXPORT_META[exportTarget] || { icon: '📤', title: 'Export', subtitle: 'Choose a period', accent: '#2196f3', accentSoft: '#eaf4fe', gradient: 'linear-gradient(135deg, #1e88e5 0%, #42a5f5 100%)' };

  const PERIOD_OPTIONS = [
    { id: 'all', icon: '✨', label: 'All Time', desc: 'Everything currently saved' },
    { id: 'yesterday', icon: '📆', label: 'Yesterday', desc: 'Previous day only' },
    { id: 'month', icon: '🗓️', label: 'This Month', desc: '1st of month to today' },
    { id: 'prev-month', icon: '🗓️', label: 'Previous Month', desc: 'Full previous month' },
    { id: 'custom', icon: '⚙️', label: 'Custom Range', desc: 'Pick start & end dates' }
  ];

  // How the recipe list is bucketed in category-grouped exports (PDF & Excel).
  const GROUPING_OPTIONS = [
    { id: 'master', icon: '🗂️', label: 'Master Category', desc: 'Group by top-level category' },
    { id: 'sub', icon: '🏷️', label: 'Subcategory', desc: 'Group by menu subcategory' }
  ];
  // The JSON backup is a flat seed file, so grouping only applies to PDF & Excel.
  const showGroupingSelector = exportTarget === 'pdf' || exportTarget === 'excel';
  // PDF & Excel always export everything; only the JSON backup offers a period.
  const showPeriodSelector = exportTarget === 'json';

  return (
    <div style={{
      position: 'fixed',
      top: '40px',
      right: '1px',
      zIndex: 3000
    }}>
      {/* Navigation Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '12px',
          backgroundColor: subscriptionLocked ? '#2196f3' : (tsrLockActive ? '#ef4444' : '#2196f3'),
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          cursor: 'pointer',
          fontSize: '20px',
          width: '50px',
          height: '50px',
          opacity: '1',
          boxShadow: subscriptionLocked ? '0 10px 24px rgba(59,130,246,0.35)' : (tsrLockActive ? '0 10px 24px rgba(239,68,68,0.35)' : '0 10px 24px rgba(59,130,246,0.35)'),
          outline: '2px solid rgba(255,255,255,0.85)',
          outlineOffset: '2px',
          position: 'relative'
        }}
      >
        {isOpen ? '✕' : '☰'}
      </button>

      {/* Navigation Menu */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '60px',
          right: '0',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          minWidth: '250px',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          {/* TSR Lock Warning Banner */}
          {tsrLockActive && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              borderBottom: '2px solid #ef4444',
              color: '#991b1b'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', fontSize: '14px' }}>
                <span>🔒</span>
                <span>TSR Recovery Mode</span>
              </div>
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#7f1d1d' }}>
                Complete wastage & TSR first
              </p>
            </div>
          )}

          {subscriptionLocked && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#eff6ff',
              borderBottom: '1px solid #bfdbfe',
              color: '#1e3a8a'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', fontSize: '14px' }}>
                <span>🔒</span>
                <span>Subscription Inactive</span>
              </div>
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#1d4ed8' }}>
                Use "My Subscription Period" below to update your subscription, or click the button below:
              </p>
              <button
                type="button"
                onClick={() => {
                  setShowMySubscription(true);
                }}
                style={{
                  marginTop: '8px',
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 4px rgba(37,99,235,0.2)'
                }}
              >
                <span>🗓️</span>
                <span>My Subscription Period</span>
              </button>
            </div>
          )}
          
          {/* Email alerts — owners only; stays reachable while the app is locked for a missed TSR. */}
          <EmailAlertsMenuRow />

          {/* Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #eee',
            backgroundColor: '#f8f9fa'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: '#000' }}>Navigation</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>Current: {currentScreen}</p>
          </div>

          {/* Health Check */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #eee'
          }}>
            <button
              onClick={checkHealth}
              disabled={isCheckingHealth || subscriptionLocked}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: (isCheckingHealth || subscriptionLocked) ? '#ccc' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: (isCheckingHealth || subscriptionLocked) ? 'not-allowed' : 'pointer',
                fontSize: '12px'
              }}
            >
              {subscriptionLocked ? '🔒 Disabled during lock' : (isCheckingHealth ? '🔄 Checking...' : '🏥 Check API Health')}
            </button>
            
            {healthStatus && (
              <div style={{ marginTop: '8px', fontSize: '11px' }}>
                {Object.entries(healthStatus).map(([service, status]) => (
                  <div key={service} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '2px 0',
                    color: status.status === 'healthy' ? '#28a745' : '#dc3545'
                  }}>
                    <span>{service}:</span>
                    <span>{status.status === 'healthy' ? '✅' : '❌'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Navigation Items */}
          {loadingPermissions ? (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              Loading permissions...
            </div>
          ) : navigationItems.length === 0 ? (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              No navigation items available
            </div>
          ) : (
            Object.entries(groupedItems).map(([category, items]) => (
              <div key={category}>
                <div style={{
                  padding: '8px 16px',
                  backgroundColor: '#f1f3f4',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#5f6368'
                }}>
                  {categories[category]} {category}
                </div>
                {items.map(item => {
                  // Check if item should be disabled during TSR lock
                  const isReviewExportItem = item.id === 'mark-as-review-export';
                  const isRecipeDownloadExportItem = item.id === 'recipe-download-export';
                  const isRecipeExcelExportItem = item.id === 'recipe-excel-export';
                  const isRecipeJsonDownloadItem = item.id === 'recipe-json-download';
                  const isIngredientsExportItem = item.id === 'ingredients-download-export';
                  const isMySubscriptionItem = item.id === 'my-subscription';
                  const isEmailLogItem = item.id === 'email-log';
                  // Both subscription tools must stay reachable while the account is locked.
                  const isSubscriptionWindowItem = item.id === 'subscription-window' || isMySubscriptionItem || isEmailLogItem;
                  const isExportItem = isReviewExportItem || isRecipeDownloadExportItem || isRecipeExcelExportItem || isRecipeJsonDownloadItem || isIngredientsExportItem || isSubscriptionWindowItem;
                  const isTsrLockDisabled = tsrLockActive
                    && !allowedScreens.includes(item.id)
                    && !isExportItem;
                  // Admin tool must stay reachable even if the admin's own account is locked.
                  const isSubscriptionDisabled = subscriptionLocked && !isSubscriptionWindowItem;
                  const isItemDisabled = isTsrLockDisabled
                    || isSubscriptionDisabled
                    || (isReviewExportItem && isExportingReviewPdf)
                    || (isRecipeDownloadExportItem && isExportingAllRecipesPdf)
                    || (isRecipeExcelExportItem && isExportingAllRecipesXlsx)
                    || (isRecipeJsonDownloadItem && isExportingJson)
                    || (isIngredientsExportItem && isExportingIngredientsXlsx);
                  
                  return (
                  <button
                    key={item.id}
                    onClick={async () => {
                      if (isItemDisabled) return;

                      if (isMySubscriptionItem) {
                        setShowMySubscription(true);
                      } else if (isEmailLogItem) {
                        setShowEmailLog(true);
                      } else if (isSubscriptionWindowItem) {
                        setShowSubscriptionWindow(true);
                      } else if (isReviewExportItem) {
                        await handleReviewPdfExport();
                      } else if (isRecipeDownloadExportItem) {
                        if (structuredBookAvailable) {
                          // Recipe Download opens the recipe IN the application, where
                          // quantities can be corrected and rows removed. The PDF is the
                          // last step of that flow, not the first — its button lives in
                          // the book's toolbar and builds from confirmed data.
                          scrollToTop();
                          goTo('structured-recipe-book');
                        } else {
                          // Fallback for non-owners and for a build with the feature off:
                          // exactly the original path — pick a grouping, then the book is
                          // BUILT and SHOWN, and downloads only if the user asks.
                          setExportTarget('pdf');
                          setShowDateModal(true);
                        }
                      } else if (isRecipeExcelExportItem) {
                        setExportTarget('excel');
                        setShowDateModal(true);
                      } else if (isRecipeJsonDownloadItem) {
                        setExportTarget('json');
                        setShowDateModal(true);
                      } else if (isIngredientsExportItem) {
                        await handleIngredientsTemplateExport();
                      } else {
                        // Reset scroll even when re-clicking the same screen.
                        scrollToTop();
                        if (currentScreen !== item.id) {
                          goTo(item.id);
                        }
                      }

                      setIsOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      backgroundColor: currentScreen === item.id ? '#e3f2fd' : 'transparent',
                      color: isItemDisabled ? '#cbd5e1' : (currentScreen === item.id ? '#1976d2' : '#000'),
                      textAlign: 'left',
                      cursor: isItemDisabled ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      borderBottom: '1px solid #f0f0f0',
                      opacity: isItemDisabled ? 0.5 : 1,
                      pointerEvents: isItemDisabled ? 'none' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (currentScreen !== item.id && !isItemDisabled) {
                        e.target.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentScreen !== item.id) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                    disabled={isItemDisabled}
                  >
                    {isReviewExportItem && isExportingReviewPdf
                      ? 'Generating Mark as Review PDF...'
                      : isRecipeDownloadExportItem && isExportingAllRecipesPdf
                        ? 'Preparing Recipe Book...'
                        : isRecipeExcelExportItem && isExportingAllRecipesXlsx
                          ? 'Generating Recipe Excel...'
                        : isRecipeJsonDownloadItem && isExportingJson
                          ? 'Generating Recipe JSON...'
                        : isIngredientsExportItem && isExportingIngredientsXlsx
                          ? 'Generating Ingredients Excel...'
                        : item.label}
                  </button>
                  );
                })}
              </div>
            ))
          )}

          {/* Footer */}
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid #eee',
            backgroundColor: '#f8f9fa',
            fontSize: '11px',
            color: '#666',
            textAlign: 'center'
          }}>
            🌐 API: {API_BASE_URL}<br/>
            📱 Frontend: localhost:3000
            <div style={{ marginTop: '8px' }}>
              <button
                onClick={async () => {
                  try {
                    const mod = await import('../services/authService');
                    await mod.default.logout();
                    window.location.reload();
                  } catch (_) {
                    window.location.reload();
                  }
                }}
                style={{
                  marginTop: '6px',
                  padding: '6px 10px',
                  fontSize: '11px',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >🚪 Sign Out</button>
            </div>
          </div>
        </div>
      )}

      {showSubscriptionWindow && (
        <SubscriptionWindowModal onClose={() => setShowSubscriptionWindow(false)} />
      )}

      {showMySubscription && (
        <MySubscriptionModal onClose={() => setShowMySubscription(false)} />
      )}

      {showEmailLog && (
        <EmailLogModal onClose={() => setShowEmailLog(false)} />
      )}

      {showDateModal && (
        <div
          className="expm-overlay"
          onClick={() => setShowDateModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '16px',
            zIndex: 9999,
            '--expm-accent': exportMeta.accent
          }}
        >
          {/* Scoped styles for smooth animations, hover & focus states */}
          <style>{`
            @keyframes expm-overlay-in { from { opacity: 0; } to { opacity: 1; } }
            @keyframes expm-pop-in { from { opacity: 0; transform: translateY(10px) scale(0.97); } to { opacity: 1; transform: none; } }
            .expm-overlay { animation: expm-overlay-in 0.18s ease-out; }
            .expm-card { animation: expm-pop-in 0.24s cubic-bezier(0.2, 0.8, 0.2, 1); }
            .expm-opt { transition: border-color 0.16s ease, background-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease; }
            .expm-opt:hover { border-color: var(--expm-accent); background-color: #f8fafc; transform: translateY(-1px); box-shadow: 0 4px 10px rgba(15,23,42,0.06); }
            .expm-date { transition: border-color 0.16s ease, box-shadow 0.16s ease; }
            .expm-date:focus { border-color: var(--expm-accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--expm-accent) 18%, transparent); }
            .expm-btn { transition: transform 0.14s ease, box-shadow 0.14s ease, background-color 0.14s ease, filter 0.14s ease; }
            .expm-btn:hover { transform: translateY(-1px); }
            .expm-btn:active { transform: translateY(0); }
            .expm-primary:hover { filter: brightness(1.06); box-shadow: 0 8px 18px color-mix(in srgb, var(--expm-accent) 40%, transparent); }
            .expm-ghost:hover { background-color: #f1f5f9; }
          `}</style>

          <div
            className="expm-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={exportMeta.title}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '18px',
              width: '100%',
              maxWidth: '420px',
              maxHeight: '88vh',
              overflowY: 'auto',
              boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.28)',
              border: '1px solid #eef2f7'
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '22px 24px 16px' }}>
              <div style={{
                width: '46px',
                height: '46px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px',
                background: exportMeta.accentSoft,
                flexShrink: 0
              }}>
                {exportMeta.icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>
                  {exportMeta.title}
                </h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '12.5px', color: '#64748b' }}>
                  {exportMeta.subtitle}
                </p>
              </div>
            </div>

            <div style={{ padding: '0 24px 22px' }}>
              {showGroupingSelector && (
                <>
                  <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', margin: '4px 0 10px' }}>
                    Group by
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
                    {GROUPING_OPTIONS.map((opt) => {
                      const selected = categoryGrouping === opt.id;
                      return (
                        <label
                          key={opt.id}
                          className="expm-opt"
                          style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '4px',
                            padding: '11px 13px',
                            borderRadius: '11px',
                            border: `1.5px solid ${selected ? exportMeta.accent : '#e5e9f0'}`,
                            backgroundColor: selected ? exportMeta.accentSoft : '#ffffff',
                            cursor: 'pointer',
                            boxShadow: selected ? '0 2px 8px rgba(15,23,42,0.06)' : 'none',
                            minWidth: 0
                          }}
                        >
                          <input
                            type="radio"
                            name="categoryGrouping"
                            value={opt.id}
                            checked={selected}
                            onChange={() => setCategoryGrouping(opt.id)}
                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                          />
                          <span style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <span style={{ fontSize: '16px', lineHeight: 1 }}>{opt.icon}</span>
                            <span style={{ fontSize: '13.5px', fontWeight: 600, color: selected ? '#0f172a' : '#334155' }}>
                              {opt.label}
                            </span>
                          </span>
                          <span style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.3 }}>
                            {opt.desc}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}

              {showPeriodSelector && (
                <>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', margin: '4px 0 10px' }}>
                Select period
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
                {PERIOD_OPTIONS.map((opt) => {
                  const selected = selectedPeriod === opt.id;
                  return (
                    <label
                      key={opt.id}
                      className="expm-opt"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '11px 14px',
                        borderRadius: '11px',
                        border: `1.5px solid ${selected ? exportMeta.accent : '#e5e9f0'}`,
                        backgroundColor: selected ? exportMeta.accentSoft : '#ffffff',
                        cursor: 'pointer',
                        boxShadow: selected ? '0 2px 8px rgba(15,23,42,0.06)' : 'none'
                      }}
                    >
                      <input
                        type="radio"
                        name="exportPeriod"
                        value={opt.id}
                        checked={selected}
                        onChange={() => setSelectedPeriod(opt.id)}
                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{ fontSize: '18px', lineHeight: 1, flexShrink: 0 }}>{opt.icon}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: selected ? '#0f172a' : '#334155' }}>
                          {opt.label}
                        </span>
                        <span style={{ display: 'block', fontSize: '11.5px', color: '#94a3b8', marginTop: '1px' }}>
                          {opt.desc}
                        </span>
                      </span>
                      <span style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: `2px solid ${selected ? exportMeta.accent : '#cbd5e1'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'border-color 0.16s ease'
                      }}>
                        {selected && (
                          <span style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: exportMeta.accent }} />
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>

              {selectedPeriod === 'custom' && (
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '18px',
                  backgroundColor: '#f8fafc',
                  padding: '14px',
                  borderRadius: '11px',
                  border: '1px solid #e5e9f0'
                }}>
                  {[{ label: 'From', value: customStart, set: setCustomStart }, { label: 'To', value: customEnd, set: setCustomEnd }].map((field) => (
                    <div key={field.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
                      <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{field.label}</span>
                      <input
                        type="date"
                        className="expm-date"
                        value={field.value}
                        onChange={(e) => field.set(e.target.value)}
                        style={{
                          padding: '9px 11px',
                          borderRadius: '8px',
                          border: '1.5px solid #cbd5e1',
                          fontSize: '13.5px',
                          color: '#334155',
                          outline: 'none',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
                </>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  className="expm-btn expm-ghost"
                  onClick={() => setShowDateModal(false)}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '10px',
                    border: '1.5px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    color: '#475569',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                >
                  Cancel
                </button>
                <button
                  className="expm-btn expm-primary"
                  onClick={handleConfirmExport}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '10px',
                    border: 'none',
                    background: exportMeta.gradient,
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    boxShadow: '0 6px 14px rgba(15,23,42,0.16)'
                  }}
                >
                  {/* The PDF is shown before it is saved, so the button promises a view,
                      not a file. Excel and JSON are not viewable and still export. */}
                  {exportTarget === 'pdf'
                    ? (isExportingAllRecipesPdf ? 'Preparing…' : 'View Recipe Book')
                    : 'Export'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The preview. Rendered last so it sits above the export modal, and only ever
          while a generated document is being held in memory. */}
      {pdfPreview && (
        <RecipePdfViewer
          blob={pdfPreview.blob}
          fileName={pdfPreview.fileName}
          pageCount={pdfPreview.pageCount}
          title="Recipe Book"
          subtitle={pdfPreview.subtitle}
          onClose={() => setPdfPreview(null)}
          onEditQuantities={structuredBookAvailable ? () => {
            setPdfPreview(null);
            scrollToTop();
            goTo('structured-recipe-book');
            setIsOpen(false);
          } : undefined}
        />
      )}
    </div>
  );
};

export default Navigation;
